import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';
import { useState, useEffect } from 'react';

const categoryArticleMap: Record<string, string[]> = {
  'remen-tuijian': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-gongneng-xiangjie', 'aiye-video-app-huiyuan-quanyi'],
  'zongyi-yule': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-huiyuan-quanyi'],
  'dianying-juchang': ['aiye-video-app-gongneng-xiangjie', 'aiye-video-app-huiyuan-quanyi', 'aiye-video-app-changjian-wenti'],
  'dianshiju': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-xiazai-anjiao', 'aiye-video-app-changjian-wenti'],
  'dongman-fanju': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-gongneng-xiangjie'],
  'tiyu-saishi': ['aiye-video-app-xiazai-anjiao', 'aiye-video-app-gongneng-xiangjie'],
  'jilupian': ['aiye-video-app-gongneng-xiangjie', 'aiye-video-app-shiyong-jiqiao'],
  'duan-shipin': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-xiazai-anjiao', 'aiye-video-app-gongneng-xiangjie'],
  'yinyue-MV': ['aiye-video-app-shiyong-jiqiao', 'aiye-video-app-gongneng-xiangjie'],
  'jiaoyu-xuexi': ['aiye-video-app-gongneng-xiangjie', 'aiye-video-app-xiazai-anjiao'],
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setSearchTerm('');
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的分类不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySlugs = categoryArticleMap[category.slug] || [];
  const relatedArticles = articles.filter((a) => categorySlugs.includes(a.slug));

  const filteredArticles = searchTerm
    ? relatedArticles.filter(
        (a) =>
          a.title.includes(searchTerm) ||
          a.excerpt.includes(searchTerm) ||
          a.tags.some((t) => t.includes(searchTerm))
      )
    : relatedArticles;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 爱液视频`,
    description: category.description,
    url: `https://aiyeshipin.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '爱液视频',
      url: 'https://aiyeshipin.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 爱液视频App | 海量${category.name}内容在线观看`}
        description={`爱液视频App${category.name}频道，${category.description}。包含${category.count}条精选内容，每日更新。`}
        keywords={`${category.name},爱液视频,${category.tags.join(',')},爱液视频app${category.name}`}
        canonicalUrl={`https://aiyeshipin.com/category/${category.slug}`}
      />
      <JsonLd data={categoryJsonLd} />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[{ label: '分类浏览', path: '/category/remen-tuijian' }, { label: category.name }]}
        />

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {category.name}
              </h1>
              <p className="text-foreground-500 text-sm">{category.description}</p>
            </div>
            <div className="relative w-full md:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索相关内容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === category.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} mr-1`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-foreground-500">
              共 {filteredArticles.length} 篇相关文章
            </span>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-xs px-2 py-1 bg-background-100 text-foreground-500 rounded-full hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line mr-1"></i>
                清除搜索
              </button>
            )}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-foreground-950 font-heading font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>
                        <i className="ri-eye-line mr-1"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-file-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">没有找到相关内容</p>
              <p className="text-foreground-400 text-sm mt-1">试试其他关键词</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}